import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { AFrameCamera, AFrameScene, BBox2D } from '../types';
import { freezeCamera, imageCoordToSpherePos } from '../utils';

interface IDrawObject {
  isDrawingMode: boolean;
  scene: AFrameScene | null;
  drawElement: HTMLElement | null;
  camera: AFrameCamera | null;
  imgWidth: number;
  imgHeight: number;
  onDone: (bbox: BBox2D) => void;
}

export const useDrawObject = ({
  isDrawingMode,
  scene,
  drawElement,
  camera,
  imgWidth,
  imgHeight,
  onDone,
}: IDrawObject) => {
  const [drawObject, setDrawObject] = useState<BBox2D | null>(null);
  const startPoint = useRef<THREE.Vector3 | null>(null);
  const endPoint = useRef<THREE.Vector3 | null>(null);
  const currentPlane = useRef<Element | null>(null);
  const isDrawing = useRef<boolean>(false);
  const isDrawingDoneRef = useRef<boolean>(false);


  const clearDrawing = useCallback(() => {
    setDrawObject(null);
    startPoint.current = null;
    endPoint.current = null;
    currentPlane.current = null;
    isDrawing.current = false;
    isDrawingDoneRef.current = false;
  }, []);


  useEffect(() => {
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const getMouseIntersection = (event: MouseEvent): THREE.Vector3 | null => {
      if (!scene) return null;
      const sceneEl = scene as any;

      const camera = sceneEl.camera;
      if (!camera || !sceneEl.renderer) return null;

      const bounds = sceneEl.renderer.domElement.getBoundingClientRect();

      mouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
      mouse.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      return raycaster.ray.intersectSphere(new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1), new THREE.Vector3()) ?? null;
    };

    const toLatLon = (vec: THREE.Vector3) => {
      const r = vec.length();
      const lat = Math.asin(vec.y / r);              // [-π/2, +π/2]
      const lon = Math.atan2(vec.x, -vec.z);         // [-π, +π]
      return { lat, lon };
    };

    const toBBox2D = (a: THREE.Vector3, b: THREE.Vector3): BBox2D => {
      const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

      const lonToX = (lon: number) => ((lon + Math.PI) / (2 * Math.PI)) * imgWidth;
      const latToY = (lat: number) => ((Math.PI / 2 - lat) / Math.PI) * imgHeight;

      const p1 = toLatLon(a);
      const p2 = toLatLon(b);

      let x1 = lonToX(p1.lon);
      let x2 = lonToX(p2.lon);
      let y1 = latToY(p1.lat);
      let y2 = latToY(p2.lat);

      x1 = clamp(x1, 0, imgWidth);
      x2 = clamp(x2, 0, imgWidth);
      y1 = clamp(y1, 0, imgHeight);
      y2 = clamp(y2, 0, imgHeight);


      const shouldCheckXMinMax = !((Math.max(x1, x2) - (imgWidth / 2)) > Math.min(x1, x2));

      return {
        xmin: shouldCheckXMinMax ? Math.min(x1, x2) : Math.max(x1, x2),
        xmax: shouldCheckXMinMax ? Math.max(x1, x2) : Math.min(x1, x2),
        ymin: Math.min(y1, y2),
        ymax: Math.max(y1, y2),
      };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (!isDrawingMode || !camera || isDrawingDoneRef.current) return;

      freezeCamera(true, camera);
      const point = getMouseIntersection(e);
      if (!point || !scene) return;

      isDrawing.current = true;
      startPoint.current = point;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDrawingMode || !camera || isDrawingDoneRef.current) return;

      if (!isDrawing.current || !startPoint.current) return;
      const point = getMouseIntersection(e);
      if (!point) return;

      endPoint.current = point;
      setDrawObject(toBBox2D(startPoint.current, endPoint.current));
    };

    const onMouseUp = () => {
      if (!isDrawingMode || !camera || isDrawingDoneRef.current) return;

      freezeCamera(false, camera);
      if (!isDrawing.current) return;
      if (!startPoint.current || !endPoint.current) {
        clearDrawing();
        return;
      };

      const bbox = toBBox2D(startPoint.current, endPoint.current);

      setDrawObject(bbox);

      startPoint.current = null;
      endPoint.current = null;
      currentPlane.current = null;
      isDrawing.current = false;
      isDrawingDoneRef.current = true;
    };

    if (isDrawingMode) {
      window.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      clearDrawing();
    }

    return () => {
      if (isDrawingMode) {
        window.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      }
    };
  }, [camera, clearDrawing, imgHeight, imgWidth, isDrawingMode, scene]);


  useLayoutEffect(() => {
    if (!drawElement) return;

    while (drawElement.firstChild) {
      drawElement.removeChild(drawElement.firstChild);
    }

    if (!drawObject) return;

    const size = {
      width: imgWidth,
      height: imgHeight,
    };

    const { xmin, ymin, xmax, ymax } = drawObject;
    const topLeft = imageCoordToSpherePos(xmin, ymin, size!.width, size!.height);
    const topRight = imageCoordToSpherePos(xmax, ymin, size!.width, size!.height);
    const bottomRight = imageCoordToSpherePos(xmax, ymax, size!.width, size!.height);
    const bottomLeft = imageCoordToSpherePos(xmin, ymax, size!.width, size!.height);

    const lines = [
      [topLeft, topRight],
      [topRight, bottomRight],
      [bottomRight, bottomLeft],
      [bottomLeft, topLeft],
    ];


    lines.forEach(([start, end]) => {
      const mid = start.clone().add(end).multiplyScalar(0.5);
      const length = start.distanceTo(end);
      const thickness = 0.01;

      const direction = end.clone().sub(start).normalize();

      const quaternion = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(1, 0, 0),
        direction
      );
      const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');

      const rotationX = THREE.MathUtils.radToDeg(euler.x);
      const rotationY = THREE.MathUtils.radToDeg(euler.y);
      const rotationZ = THREE.MathUtils.radToDeg(euler.z);

      // Create a thin a-plane
      const plane = document.createElement('a-plane');
      plane.setAttribute('width', length.toString());
      plane.setAttribute('height', thickness.toString());
      plane.setAttribute('color', 'blue');
      plane.setAttribute('material', 'shader: flat; side: double');
      plane.setAttribute('position', `${mid.x} ${mid.y} ${mid.z}`);
      plane.setAttribute('rotation', `${rotationX} ${rotationY} ${rotationZ}`);

      drawElement.appendChild(plane);
    });

    // === Action buttons (only while drawing) ===
    if (isDrawingDoneRef.current) {
      const horizontalDir = bottomRight.clone().sub(bottomLeft).normalize();
      const verticalDir = bottomRight.clone().sub(topRight).normalize();

      const underOffset = 0.04;      // distance below the rectangle
      const padRight = 0.0;         // how far left from the corner the first button goes
      const buttonGap = 0.09;        // gap between buttons

      const base = bottomRight.clone().add(verticalDir.clone().multiplyScalar(underOffset));
      const donePos = base.clone().add(horizontalDir.clone().multiplyScalar(-padRight));
      const cancelPos = base.clone().add(horizontalDir.clone().multiplyScalar(-(padRight + buttonGap)));

      const actionsGroup = document.createElement('a-entity');
      drawElement.appendChild(actionsGroup);

      const mkButton = (iconId: string, pos: THREE.Vector3, color: string) => {
        // background plate
        const bg = document.createElement('a-plane');

        const direction = topRight.clone().sub(topLeft).normalize();
        const quaternion = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(1, 0, 0),
          direction
        );
        const euler = new THREE.Euler().setFromQuaternion(quaternion, 'YXZ');

        const rotationX = THREE.MathUtils.radToDeg(euler.x);
        const rotationY = THREE.MathUtils.radToDeg(euler.y);
        const rotationZ = THREE.MathUtils.radToDeg(euler.z);

        bg.setAttribute('width', '0.08');
        bg.setAttribute('height', '0.05');
        bg.setAttribute('material', `shader: flat; color: ${color}; opacity: 1; side: double`);
        bg.setAttribute('position', `${pos.x} ${pos.y} ${pos.z}`);
        bg.classList.add('clickable');
        bg.setAttribute('rotation', `${rotationX} ${rotationY} ${rotationZ}`);

        const icon = document.createElement('a-image');
        icon.setAttribute('src', `#${iconId}`);   // preload your ✓ PNG/SVG
        icon.setAttribute('width', '0.03');
        icon.setAttribute('height', '0.03');
        icon.setAttribute('position', '0 0 0');
        icon.setAttribute('material', 'shader: flat; transparent: true; depthTest: false; depthWrite: false');
        (icon as any).object3D.renderOrder = 2;

        bg.appendChild(icon);
        actionsGroup.appendChild(bg);
        return bg;
      };

      const doneBtn = mkButton('doneIcon', donePos, '#22c55e');   // green
      const cancelBtn = mkButton('cancelIcon', cancelPos, '#ef4444'); // red

      // Wire events
      const handleDone = () => {
        if (!drawObject) return;
        onDone(drawObject);
      };
      const handleCancel = () => {
        clearDrawing();
      };

      doneBtn.addEventListener('click', handleDone);
      cancelBtn.addEventListener('click', handleCancel);

      // Cleanup button listeners on rerender
      return () => {
        doneBtn.removeEventListener('click', handleDone);
        cancelBtn.removeEventListener('click', handleCancel);
      };
    }

  }, [drawObject, imgWidth, imgHeight, drawElement, clearDrawing, onDone]);



  return {
    drawObject,
    isDrawing,
    clearDrawing,
  };
};
